import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Divider,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { HomeExpenseFields, HomeExpenseGroupDto, AccountDto } from '../../types/homeExpenses';
import HomeExpenseGroupSelect from '../../components/inputs/HomeExpenseGroupSelect';

interface MultiExpenseFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (expenses: HomeExpenseFields[]) => Promise<void>;
  groups: HomeExpenseGroupDto[];
  accounts: AccountDto[];
  loading?: boolean;
}

interface ExpenseFormData extends HomeExpenseFields {
  tempId: string; // For tracking rows
}

const MultiExpenseForm: React.FC<MultiExpenseFormProps> = ({
  open,
  onClose,
  onSubmit,
  groups,
  accounts,
  loading = false
}) => {
  const [expenses, setExpenses] = useState<ExpenseFormData[]>(() => [
    {
      tempId: `temp-${Date.now()}`,
      date: format(new Date(), 'yyyy-MM-dd'),
      homeExpenseGroupPublicId: '',
      amount: 0,
      accountPublicId: accounts.find(a => a.isPrimary)?.publicId || '',
      observations: '',
    }
  ]);
  const [error, setError] = useState<string | null>(null);

  const addExpenseRow = useCallback(() => {
    // Add new row at the top (unshift behavior)
    setExpenses(prev => [
      {
        tempId: `temp-${Date.now()}`,
        date: format(new Date(), 'yyyy-MM-dd'),
        homeExpenseGroupPublicId: '',
        amount: 0,
        accountPublicId: accounts.find(a => a.isPrimary)?.publicId || '',
        observations: '',
      },
      ...prev
    ]);
    // Scroll to top of dialog to reveal newly added row
    const container = document.querySelector('[role="dialog"] .MuiDialogContent-root');
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [accounts]);

  // If accounts load after dialog opens, populate any blank accountPublicId with primary
  useEffect(() => {
    const primary = accounts.find(a => a.isPrimary);
    if (primary) {
      setExpenses(prev => prev.map(e => !e.accountPublicId ? { ...e, accountPublicId: primary.publicId } : e));
    }
  }, [accounts]);

  const removeExpenseRow = useCallback((tempId: string) => {
    setExpenses(prev => prev.filter(expense => expense.tempId !== tempId));
  }, []);

  const updateExpenseRow = useCallback((tempId: string, field: keyof HomeExpenseFields, value: string | number) => {
    setExpenses(prev => prev.map(expense => 
      expense.tempId === tempId 
        ? { ...expense, [field]: value }
        : expense
    ));
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);
    
    // Validate all rows
    const validExpenses = expenses.filter(expense => 
      expense.homeExpenseGroupPublicId && 
      expense.accountPublicId && 
      expense.amount > 0
    );

    if (validExpenses.length === 0) {
      setError('Adicione pelo menos uma despesa válida com todos os campos obrigatórios preenchidos.');
      return;
    }

    if (validExpenses.length !== expenses.length) {
      setError(`${expenses.length - validExpenses.length} despesa(s) serão ignoradas por falta de campos obrigatórios.`);
    }

    try {
      // Remove tempId before sending to API
      const expenseCommands = validExpenses.map(({ tempId, ...expense }) => expense);
      await onSubmit(expenseCommands);
      
      // Reset form on success
      setExpenses([{
        tempId: `temp-${Date.now()}`,
        date: format(new Date(), 'yyyy-MM-dd'),
        homeExpenseGroupPublicId: '',
        amount: 0,
        accountPublicId: accounts.find(a => a.isPrimary)?.publicId || '',
        observations: '',
      }]);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível criar as despesas');
    }
  }, [expenses, onSubmit, onClose, accounts]);

  const handleClose = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  const sortedGroups = groups.sort((a, b) => a.name.localeCompare(b.name));
  const sortedAccounts = accounts.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Adicionar várias despesas</Typography>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pb: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2, position: 'sticky', top: 0, background: 'background.paper', zIndex: 2, pt: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addExpenseRow}
            disabled={loading}
            size="small"
          >
            Adicionar linha (no topo)
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {expenses.map((expense, index) => (
            <Card key={expense.tempId} variant="outlined" sx={{ p: 0 }}>
              <CardContent sx={{ py: 1.5, px: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                    #{index + 1}
                  </Typography>
                  {expenses.length > 1 && (
                    <IconButton
                      color="error"
                      onClick={() => removeExpenseRow(expense.tempId)}
                      disabled={loading}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  <Box sx={{ minWidth: 200, flex: '1 1 300px' }}>
                    <TextField
                      fullWidth
                      label="Data"
                      type="date"
                      value={expense.date}
                      onChange={(e) => updateExpenseRow(expense.tempId, 'date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={loading}
                      required
                    />
                  </Box>

                  <Box sx={{ minWidth: 200, flex: '1 1 300px' }}>
                    <HomeExpenseGroupSelect
                      groups={groups}
                      value={expense.homeExpenseGroupPublicId}
                      onChange={(val) => updateExpenseRow(expense.tempId, 'homeExpenseGroupPublicId', val)}
                      size="small"
                      includeEmpty
                      required
                      disabled={loading}
                    />
                  </Box>

                  <Box sx={{ minWidth: 150, flex: '1 1 200px' }}>
                    <TextField
                      fullWidth
                      label="Valor"
                      type="number"
                      value={expense.amount || ''}
                      onChange={(e) => updateExpenseRow(expense.tempId, 'amount', parseFloat(e.target.value) || 0)}
                      disabled={loading}
                      required
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Box>

                  <Box sx={{ minWidth: 200, flex: '1 1 400px' }}>
                    <FormControl fullWidth required>
                      <InputLabel>Conta</InputLabel>
                      <Select
                        value={expense.accountPublicId}
                        onChange={(e) => updateExpenseRow(expense.tempId, 'accountPublicId', e.target.value)}
                        label="Conta"
                        disabled={loading}
                      >
                        {sortedAccounts.map((account) => (
                          <MenuItem key={account.publicId} value={account.publicId}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: account.color || '#9e9e9e',
                                }}
                              />
                              {account.name}
                              {account.isPrimary && (
                                <Chip label="Principal" size="small" color="primary" />
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ minWidth: 160, flex: '1 1 100%' }}>
                    <TextField
                      fullWidth
                      label="Observações"
                      multiline
                      rows={1}
                      value={expense.observations}
                      onChange={(e) => updateExpenseRow(expense.tempId, 'observations', e.target.value)}
                      disabled={loading}
                      placeholder="Notas opcionais sobre esta despesa..."
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={loading || expenses.every(e => !e.homeExpenseGroupPublicId || !e.accountPublicId || e.amount <= 0)}
        >
          {loading ? 'A criar...' : `Criar ${expenses.length} ${expenses.length !== 1 ? 'despesas' : 'despesa'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiExpenseForm;
